import React, { useCallback, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
    closestCenter,
    DndContext,
    DragEndEvent,
    DragOverlay,
    DragStartEvent,
    PointerSensor,
    UniqueIdentifier,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import {
    horizontalListSortingStrategy,
    SortableContext,
} from "@dnd-kit/sortable";
import useMediaQuery from "@mui/material/useMediaQuery";
import { observer } from "mobx-react";
import styled, { keyframes } from "styled-components";

import { DefinedEntity } from "@formant/core/model/DefinedEntity";
import { IEventFilter } from "@formant/core/model/IEventFilter";
import { IFilter } from "@formant/core/model/IFilter";
import { IGroup } from "@formant/core/model/IGroup";
import { ITags } from "@formant/core/model/ITags";
import { IView } from "@formant/core/model/IView";
import { LayoutType } from "@formant/core/model/LayoutType";
import { Uuid } from "@formant/core/model/Uuid";

import { useStores } from "../../../context/StoreContext";
import { getPortalElement } from "../../../utils/getPortalElement";
import { namedIndexedComparator } from "../../../utils/indexedItemUtils";
import { F3AddView } from "./F3.AddView";
import { mobileBreakpoint } from "./F3.HomeTabs.styles";
import { F3OverflowMenu } from "./F3.OverflowMenu";
import { getIconFromLayoutType } from "./F3.TabIcon";
import { TabItem } from "./F3.TabItem";
import { TabList } from "./F3.TabList";
import { useViewReorderingCallbacks } from "./UseViewReorderingCallbacks";

const placeholderView = {
    name: "",
    id: "00000000-0000-0000-0000-000000000000",
    layoutType: undefined,
} as const;

const FloatingTabItem = styled(TabItem)`
    animation: ${keyframes`
      0% { transform: scale(1); }
      100% { transform: scale(1.05); }
    `} 200ms forwards;
`;

function noop() {}

interface IHomeTabsProps {
    context: IFilter;
    eventFilter: IEventFilter;
    value?: Uuid;
    device: {
        id: string;
        tags: ITags;
    } | null;
    filter: IFilter;
    filterActive: boolean;
    layoutType?: LayoutType;
    onEditDevice: (() => void) | null;
    onChangeViewId?: (id: Uuid) => void;
    onToggleFilter?: () => void;
    onRetrieveOnDemandData?: () => void;
    group?: IGroup;
    views: IView[];
}

export const F3HomeTabs = observer(function HomeTabs(props: IHomeTabsProps) {
    const {
        device,
        filter,
        filterActive,
        onChangeViewId,
        onToggleFilter,
        onRetrieveOnDemandData,
        value,
        views,
        group,
    } = props;

    const { appStore, authStore, viewStore } = useStores();

    const navigatorEnabled = false;
    const hasTeleopViews = authStore.hasAccessByRole("operator");

    const filteredViews = useMemo(
        () =>
            views
                .filter((_): _ is DefinedEntity<IView> => {
                    const initialFilter =
                        !_.url ||
                        _.id === value ||
                        (_.url && _.showOnSingleDevice);

                    if (!initialFilter) {
                        return false;
                    }

                    if (_.layoutType === "teleop") {
                        return !navigatorEnabled ? false : hasTeleopViews;
                    }

                    if (!_.id) {
                        return false;
                    }

                    return true;
                })
                .sort(namedIndexedComparator),
        [views, value, navigatorEnabled, hasTeleopViews],
    );

    const [isOverflown, setIsOverflown] = useState(false);
    const [isReorderingTabs, setIsReorderingTabs] = useState(false);

    const isMobile = useMediaQuery(mobileBreakpoint);

    const deviceIds = useMemo(
        () => (device ? [device.id] : [...(filter?.deviceIds ?? [])]),
        [device, filter],
    );

    const tags = useMemo(
        () =>
            group ? { [group.tagKey]: group.tagValue } : (device?.tags ?? {}),
        [device],
    );

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 2,
            },
        }),
    );

    const [dragViewId, setDragViewId] = useState<UniqueIdentifier | null>(null);
    const dragView = useMemo(() => {
        const foundView =
            dragViewId !== null
                ? filteredViews.find(it => it.id === dragViewId)
                : undefined;

        return foundView ?? placeholderView;
    }, [dragViewId]);

    const handleDragStart = useCallback(
        (event: DragStartEvent) => {
            setDragViewId(event.active.id);
        },
        [setDragViewId],
    );

    const handleDragEnd = useCallback(
        async (event: DragEndEvent) => {
            setDragViewId(null);
            const { active, over, delta } = event;
            if (!over || active.id === over.id) {
                return;
            }

            setIsReorderingTabs(true);
            try {
                await viewStore.reorder(
                    active.id as Uuid,
                    delta.x < 0 ? "before" : "after",
                    over.id as Uuid,
                );
            } catch (err) {
                console.error("Failed reordering views", { err });
                appStore.setSnackbar("Failed reordering views");
            } finally {
                setIsReorderingTabs(false);
            }
        },
        [setDragViewId, appStore, viewStore, setIsReorderingTabs],
    );

    const {
        handleMoveToStart,
        handleMoveToEnd,
        handleMoveLeft,
        handleMoveRight,
    } = useViewReorderingCallbacks(filteredViews);

    const isReorderable =
        !isMobile && authStore.hasAccess("views", "administrator");

    return (
        <>
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToHorizontalAxis]}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
            >
                <SortableContext
                    items={filteredViews}
                    strategy={horizontalListSortingStrategy}
                >
                    <TabList
                        deviceId={device?.id}
                        filterActive={filterActive}
                        isReorderable={isReorderable}
                        isSaving={isReorderingTabs}
                        value={value}
                        views={filteredViews}
                        dragViewId={dragViewId || undefined}
                        onToggleFilter={onToggleFilter}
                        onChangeViewId={onChangeViewId}
                        onRetrieveOnDemandData={onRetrieveOnDemandData}
                        setIsOverflown={setIsOverflown}
                        onMoveToStart={handleMoveToStart}
                        onMoveToEnd={handleMoveToEnd}
                        onMoveLeft={handleMoveLeft}
                        onMoveRight={handleMoveRight}
                    />
                </SortableContext>
                {createPortal(
                    <DragOverlay dropAnimation={null}>
                        <FloatingTabItem
                            icon={getIconFromLayoutType(
                                dragView.layoutType ?? "observe",
                            )}
                            isFloating={true}
                            filtering={filterActive}
                            isSelected={dragView.id === value}
                            onOpenDropdown={
                                dragView.id === value ? noop : undefined
                            }
                        >
                            {dragView.name}
                        </FloatingTabItem>
                    </DragOverlay>,
                    getPortalElement(),
                )}
            </DndContext>
            {(isOverflown || isMobile) && (
                <F3OverflowMenu
                    value={value}
                    items={filteredViews}
                    onChangeViewId={onChangeViewId}
                />
            )}
            {authStore.hasAccess("views", "administrator") && (
                <F3AddView
                    onViewCreated={onChangeViewId}
                    group={group}
                    tags={tags}
                    deviceIds={deviceIds}
                />
            )}
        </>
    );
});
